import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import { uiLoggerNamesInvoiceRejection } from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { TagsInputUniqueKeys } from "../../ReconMIS/AutoCompletes";
import { InternalResolutionStatus } from "./RejectedInvoices";

import { FixedSizeList, ListChildComponentProps } from "react-window";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const option = data[index];

  const mergedStyle = {
    ...option.props.style, // ✅ preserves color/fontWeight from renderOption
    ...style, // ✅ includes positioning from react-window
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return React.cloneElement(option, {
    ...option.props,
    style: mergedStyle,
  });
}

export const VirtualizedListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const items = React.Children.toArray(children);
    const ITEM_SIZE = 40;
    const height = Math.min(8, items.length) * ITEM_SIZE + 2 * LISTBOX_PADDING;
    const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => <div ref={ref} {...props} {...other} />);
    OuterElementType.displayName = "OuterElementType";
    return (
      <div ref={ref}>
        <FixedSizeList
          height={height}
          itemCount={items.length}
          itemSize={ITEM_SIZE}
          width="100%"
          itemData={items}
          outerElementType={OuterElementType}
          innerElementType="ul"
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  }
);

export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer); // cleanup on new keystroke
  }, [value, delay]);

  return debouncedValue;
}

const EXCLUDE = "Exclude";

const FilterOfRejectedInvoices = ({
  openFilterOfRejectedInvoices,
  setOpenFilterOfRejectedInvoices,
  filters,
  setFilters,
  tabValue,
  listOpenInvoices,
  listCloseInvoices,
  allDropDownValues,
}) => {
  const getFilterValue = (filters: any[], id: string): string[] => {
    const filter = filters.find((f) => f.id === id);
    return Array.isArray(filter?.value) ? filter.value.filter((v) => v !== null && v !== undefined && v !== "") : [];
  };

  const [vendorNames, setVendorNames] = useState<string[]>(getFilterValue(filters, "vendorName"));
  const [vendorCodes, setVendorCodes] = useState<string[]>(getFilterValue(filters, "vendorCode"));
  const [pan, setPan] = useState<string[]>(getFilterValue(filters, "pan"));
  const [grnNumber, setGrnNumber] = useState<string[]>(getFilterValue(filters, "grnNumber"));
  const [invoiceNumber, setInvoiceNumber] = useState<string[]>(getFilterValue(filters, "invoiceNumber"));
  const [issueStatus, setIssueStatus] = useState<string[]>(getFilterValue(filters, "issueStatus"));
  const [commentsFilter, setCommentsFilter] = useState<string[]>(getFilterValue(filters, "commentStatus"));
  const [outletName, setOutletName] = useState<string[]>(getFilterValue(filters, "outletName"));
  const [outletId, setOutletId] = useState<string[]>(getFilterValue(filters, "outletId"));

  //  performance optimization
  const [grnInput, setGrnInput] = useState("");
  const [panInput, setPanInput] = useState("");
  const [vendorCodeInput, setVendorCodeInput] = useState("");
  const [vendorNameInput, setVendorNameInput] = useState("");
  const [invoiceInput, setInvoiceInput] = useState("");
  const [outletNameInput, setOutletNameInput] = useState("");
  const [outletIdInput, setOutletIdInput] = useState("");
  const debouncedGrnInput = useDebounce(grnInput, 200);
  const debouncedPanInput = useDebounce(panInput, 200);
  const debouncedVendorCodeInput = useDebounce(vendorCodeInput, 200);
  const debouncedVendorNameInput = useDebounce(vendorNameInput, 200);
  const debouncedInvoiceInput = useDebounce(invoiceInput, 200);
  const debouncedOutletNameInput = useDebounce(outletNameInput, 200);
  const debouncedOutletIdInput = useDebounce(outletIdInput, 200);

  const handleClose = () => {
    setOpenFilterOfRejectedInvoices(false);
  };

  const handleApplyFilter = () => {
    const newFilters = [];

    if (vendorNames.length > 0) {
      newFilters.push({ id: "vendorName", value: vendorNames });
    }

    if (vendorCodes.length > 0) {
      newFilters.push({ id: "vendorCode", value: vendorCodes });
    }

    if (pan.length > 0) {
      newFilters.push({ id: "pan", value: pan });
    }

    if (grnNumber.length > 0) {
      newFilters.push({ id: "grnNumber", value: grnNumber });
    }
    if (outletName.length > 0) {
      newFilters.push({ id: "outletName", value: outletName });
    }
    if (outletId.length > 0) {
      newFilters.push({ id: "outletId", value: outletId });
    }

    if (invoiceNumber.length > 0) {
      newFilters.push({ id: "invoiceNumber", value: invoiceNumber });
    }

    if (issueStatus.length > 0) {
      newFilters.push({ id: "issueStatus", value: issueStatus });
    }

    if (commentsFilter[0] === "Unread") {
      newFilters.push({ id: "commentStatus", value: ["Unread"] });
    } else if (commentsFilter[0] === "Read") {
      newFilters.push({ id: "commentStatus", value: ["Read"] });
    }
    // commentsFilter[0] === "All" => skip filter

    // Apply filters to MRT
    setFilters(newFilters);
    if (tabValue === 0) {
      listOpenInvoices(newFilters);
    } else {
      listCloseInvoices(newFilters);
    }
    handleClose();

    uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_FILTER_APPLY_CLICK.functionName, null, null, {
      message: uiLoggerNamesInvoiceRejection.UI_SST_IR_FILTER_APPLY_CLICK.message,
    });
  };

  const handleClearFilters = () => {
    setFilters([]);
    setVendorNames([]);
    setVendorCodes([]);
    setPan([]);
    setGrnNumber([]);
    setOutletName([]);
    setOutletId([]);
    setInvoiceNumber([]);
    setCommentsFilter(["All"]);

    if (tabValue === 0) {
      listOpenInvoices([], []);
    } else {
      listCloseInvoices([], []);
    }

    handleClose();
  };

  function extractUniqueOptions<T extends string>(data: T[] = [], field: string): { [key: string]: T }[] {
    const values = [EXCLUDE, ...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    return values.map((val) => ({ [field]: val } as { [key: string]: T }));
  }

  const allGrnOptions = useMemo(() => {
    return extractUniqueOptions<string>(allDropDownValues.grnNumbers, "grnNumber");
  }, [allDropDownValues.grnNumbers]);

  const allOutletNameOptions = useMemo(() => {
    return extractUniqueOptions<string>(allDropDownValues?.outletNames, "outletName");
  }, [allDropDownValues.outletNames]);

  const allOutletIdOptions = useMemo(() => {
    return extractUniqueOptions<string>(
      allDropDownValues?.outletIds?.map((id) => id.toString()),
      "outletId"
    );
  }, [allDropDownValues.outletIds]);

  const allPanOptions = useMemo(
    () => extractUniqueOptions<string>(allDropDownValues.panNumbers, "pan"),
    [allDropDownValues.panNumbers]
  );

  const allVendorCodeOptions = useMemo(
    () => extractUniqueOptions<string>(allDropDownValues.vendorCodes, "vendorCode"),
    [allDropDownValues.vendorCodes]
  );

  const allVendorNameOptions = useMemo(
    () => extractUniqueOptions<string>(allDropDownValues.vendorNames, "vendorName"),
    [allDropDownValues.vendorNames]
  );

  const allInvoiceOptions = useMemo(
    () => extractUniqueOptions<string>(allDropDownValues.invoiceNumbers, "invoiceNumber"),
    [allDropDownValues.invoiceNumbers]
  );

  const grnNumberOptions = useMemo(() => {
    if (!debouncedGrnInput) return allGrnOptions.slice(0, 100);
    return allGrnOptions.filter((opt) => opt.grnNumber.toLowerCase().includes(debouncedGrnInput.toLowerCase()));
  }, [allGrnOptions, debouncedGrnInput]);

  const outletNameOptions = useMemo(() => {
    if (!debouncedOutletNameInput) return allOutletNameOptions.slice(0, 100);
    return allOutletNameOptions.filter((opt) =>
      opt.outletName.toLowerCase().includes(debouncedOutletNameInput.toLowerCase())
    );
  }, [allOutletNameOptions, debouncedOutletNameInput]);

  const outletIdOptions = useMemo(() => {
    if (!debouncedOutletIdInput) return allOutletIdOptions.slice(0, 100);
    return allOutletIdOptions.filter((opt) =>
      opt.outletId.toLowerCase().includes(debouncedOutletIdInput.toLowerCase())
    );
  }, [allOutletIdOptions, debouncedOutletIdInput]);

  const panOptions = useMemo(() => {
    if (!debouncedPanInput) return allPanOptions.slice(0, 100);
    return allPanOptions.filter((opt) => opt.pan.toLowerCase().includes(debouncedPanInput.toLowerCase()));
  }, [allPanOptions, debouncedPanInput]);

  const vendorCodeOptions = useMemo(() => {
    if (!debouncedVendorCodeInput) return allVendorCodeOptions.slice(0, 100);
    return allVendorCodeOptions.filter((opt) =>
      opt.vendorCode.toLowerCase().includes(debouncedVendorCodeInput.toLowerCase())
    );
  }, [allVendorCodeOptions, debouncedVendorCodeInput]);

  const vendorNameOptions = useMemo(() => {
    if (!debouncedVendorNameInput) return allVendorNameOptions.slice(0, 100);
    return allVendorNameOptions.filter((opt) =>
      opt.vendorName.toLowerCase().includes(debouncedVendorNameInput.toLowerCase())
    );
  }, [allVendorNameOptions, debouncedVendorNameInput]);

  const invoiceNumberOptions = useMemo(() => {
    if (!debouncedInvoiceInput) return allInvoiceOptions.slice(0, 100);
    return allInvoiceOptions.filter((opt) =>
      opt.invoiceNumber.toLowerCase().includes(debouncedInvoiceInput.toLowerCase())
    );
  }, [allInvoiceOptions, debouncedInvoiceInput]);

  return (
    <div>
      <Dialog open={openFilterOfRejectedInvoices} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Filter</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Name</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={vendorNames}
                setTags={setVendorNames}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Vendor Name"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={vendorNameOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.vendorName === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.vendorName}
                  </li>
                )}
                accessor={"vendorName"}
                uniqueKeyAccessor={"vendorName"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={vendorNameInput}
                onInputChange={(_, val) => setVendorNameInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Code</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={vendorCodes}
                setTags={setVendorCodes}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Vendor Code"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={vendorCodeOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.vendorCode === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.vendorCode}
                  </li>
                )}
                accessor={"vendorCode"}
                uniqueKeyAccessor={"vendorCode"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={vendorCodeInput}
                onInputChange={(_, val) => setVendorCodeInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">PAN</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={pan}
                setTags={setPan}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select PAN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={panOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.pan === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.pan}
                  </li>
                )}
                accessor={"pan"}
                uniqueKeyAccessor={"pan"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={panInput}
                onInputChange={(_, val) => setPanInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">GRN Number</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={grnNumber}
                setTags={setGrnNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select GRN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={grnNumberOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.grnNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.grnNumber}
                  </li>
                )}
                accessor={"grnNumber"}
                uniqueKeyAccessor={"grnNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={grnInput}
                onInputChange={(_, val) => setGrnInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Invoice Number</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={invoiceNumber}
                setTags={setInvoiceNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Invoice Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={invoiceNumberOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.invoiceNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.invoiceNumber}
                  </li>
                )}
                accessor={"invoiceNumber"}
                uniqueKeyAccessor={"invoiceNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={invoiceInput}
                onInputChange={(_, val) => setInvoiceInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Resolution Status</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={issueStatus} // State for selected resolution statuses
                setTags={setIssueStatus} // Function to update the state
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Resolution Status"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={Object.values(InternalResolutionStatus).map((status) => ({
                  issueStatus: status,
                }))} // Map InternalResolutionStatus values to objects
                accessor={"issueStatus"}
                uniqueKeyAccessor={"issueStatus"}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Outlet Name</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={outletName}
                setTags={setOutletName}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Outlet Name"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={outletNameOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.outletName === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.outletName}
                  </li>
                )}
                accessor={"outletName"}
                uniqueKeyAccessor={"outletName"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={outletNameInput}
                onInputChange={(_, val) => setOutletNameInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Store Id</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={outletId}
                setTags={setOutletId}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Store Id"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={outletIdOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.outletId === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.outletId}
                  </li>
                )}
                accessor={"outletId"}
                uniqueKeyAccessor={"outletId"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={outletIdInput}
                onInputChange={(_, val) => setOutletIdInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Comments</Typography>
            <Box sx={{ width: "400px" }}>
              <RadioGroup
                className="space_between"
                row
                defaultValue="All" // Default selected value
                value={commentsFilter[0]} // Controlled value
                onChange={(e) => {
                  console.log(e.target.value); // Handle the selected value (all, unread, or read)
                  setCommentsFilter([e.target.value]); // Update the state with the selected value
                }}
              >
                <FormControlLabel value="All" control={<Radio size="small" />} label="All" />
                <FormControlLabel value="Unread" control={<Radio size="small" />} label="Unread" />
                <FormControlLabel value="Read" control={<Radio size="small" />} label="Read" />
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="text" onClick={handleClearFilters}>
            Clear Filter
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            //    disabled={isEditing}
            //    startIcon={<LoadingIcon loading={isEditing} />}
            onClick={() => {
              handleApplyFilter();
            }}
          >
            Apply
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FilterOfRejectedInvoices;
